import { CampaignType } from '@dev/ee-api/ee-api-models';
import {
    ContentLoader,
    CustomTable,
    CustomTableHeadersProps,
    CustomTableRowsProps,
    EmptyState,
    formatNumber,
    MobileCardRowsProps,
    MobileTable,
    Paper,
    TertiaryButton,
    Typography,
} from '@dev/ee-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { map } from 'lodash-es';
import { push as Push } from 'redux-first-history';
import { ReportsState } from '@/store/reports/types';
import { CampaignsPaths } from '@/services/dictionaries/Paths';
import useMobile from '@/services/hooks/useMobile';

const ScreenWrapper = styled.div`
    padding: 32px 0px;
    position: relative;
`;

const HeaderWithButton = styled.div`
    display: flex;
    justify-content: space-between;
    padding-bottom: 32px;
`;

const MobileTableContainer = styled.div`
    .MuiTableRow-root {
        vertical-align: baseline;
    }
`;

const useTableHeaders = (): (CustomTableHeadersProps | string)[] => {
    const { t } = useTranslation();
    const isMobile = useMobile();

    if (isMobile) {
        return [t('links'), t('clicks')];
    }

    return [
        {
            id: 'linkName',
            label: t('links'),
        },
        {
            id: 'click',
            label: t('clicks'),
            headerAlign: 'right' as const,
        },
    ];
};

const useTableRows = (reports: ReportsState): CustomTableRowsProps[] | MobileCardRowsProps[] => {
    const isMobile = useMobile();

    const SkeletonTemplate = {
        Links: <ContentLoader mode="row" />,
        Clicks: <ContentLoader mode="row" />,
    };

    const SkeletonRows = map([...new Array(4)], (): MobileCardRowsProps => SkeletonTemplate);

    const rows = map(
        reports.linkTrackingList.slice(0, 4),
        (singleLinkTrackingRow): MobileCardRowsProps => ({
            links: singleLinkTrackingRow.Link,
            clicks: formatNumber(parseInt(singleLinkTrackingRow.Clicks, 10)),
        })
    );
    if (isMobile) {
        return reports.linkTrackingLoading ? SkeletonRows : rows;
    }

    return map(reports?.linkTrackingList?.slice(0, 4), (singleLink) => ({
        linkName: singleLink.Link,
        clicks: <div style={{ textAlign: 'right' }}>{formatNumber(parseInt(singleLink.Clicks, 10))}</div>,
        rowID: singleLink.Link,
    }));
};

const MobileTableWithLinks = (props: {
    headers: string[];
    rows: MobileCardRowsProps[];
    isEmpty: boolean;
}): JSX.Element => {
    const { t } = useTranslation();
    const { headers, isEmpty, rows } = props;

    if (isEmpty) {
        return (
            <Paper>
                <EmptyState text={t('emptystate')} />
            </Paper>
        );
    }
    return (
        <MobileTableContainer>
            <MobileTable isWrapTextEnabled={true} headers={headers as string[]} rows={rows} />
        </MobileTableContainer>
    );
};

export const TableWithLinks = (props: { name: string; type: CampaignType; reports: ReportsState }): JSX.Element => {
    const { t } = useTranslation();
    const { reports, name, type } = props;
    const dispatch = useDispatch();
    const isMobile = useMobile();

    const headers = useTableHeaders();
    const rows = useTableRows(reports);
    const columnsWidth = { linkName: '80%', clicks: '20%' };

    const isEmpty = !reports?.linkTrackingList?.length && !reports?.linkTrackingLoading;

    return (
        <ScreenWrapper>
            <HeaderWithButton>
                <Typography variant="h1">{t('mostpopularlinks')}</Typography>
                {!isEmpty && (
                    <TertiaryButton
                        text={t('seeall')}
                        onClick={(): void => {
                            dispatch(Push(CampaignsPaths.CampaignLinks.replace(':type', type).replace(':name', name)));
                        }}
                    />
                )}
            </HeaderWithButton>
            {isMobile ? (
                <MobileTableWithLinks headers={headers as string[]} rows={rows} isEmpty={isEmpty} />
            ) : (
                <Paper padding={{ top: 24, bottom: 32 }}>
                    <CustomTable
                        headers={headers as CustomTableHeadersProps[]}
                        rows={!reports?.isLoadingLinks && rows}
                        isEmpty={isEmpty}
                        loading={reports?.isLoadingLinks}
                        stickyHeader
                        rowPadding={48}
                        rowHeight={60}
                        hideCheckboxes
                        columnsWidth={columnsWidth}
                        emptyStateProps={{ text: t('emptystate') }}
                        disableRowClick
                        disableFirstRowBorderTop
                    />
                </Paper>
            )}
        </ScreenWrapper>
    );
};
